'use client';

import { ChevronDown, LoaderCircle, Sparkles } from 'lucide-react';
import Link from 'next/link';
import { type ReactNode, useState } from 'react';

import {
	type CandidateEvent,
	type CandidateEventContext,
	formatScore,
	formatTaskDuration,
	reportLabel,
} from '@/features/candidate-interview';
import type {
	CandidateInterviewReportEnvelopeV1,
	CandidateInterviewReportTaskV1,
} from '@/shared/api';
import { ChatMarkdown, TheoryMarkdown } from '@/shared/ui/markdown';

type ReportVariant = 'legacy' | 'workspace';

export type CandidateTrack = (
	event: CandidateEvent,
	context?: CandidateEventContext,
) => void;
const focus =
	'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-4';

function ReportSection({
	title,
	score,
	positive,
	children,
	initialOpen = false,
	variant = 'legacy',
	onToggle,
}: {
	title: string;
	score?: string;
	positive?: boolean;
	children: ReactNode;
	initialOpen?: boolean;
	variant?: ReportVariant;
	onToggle: (open: boolean) => void;
}) {
	const workspace = variant === 'workspace';
	const [open, setOpen] = useState(initialOpen);
	return (
		<section
			className={
				workspace
					? 'border-b border-t border-gray-4 [&+section]:border-t-0'
					: 'border-t border-gray-200'
			}
		>
			<button
				type="button"
				aria-expanded={open}
				onClick={() => {
					setOpen(!open);
					onToggle(!open);
				}}
				className={`flex w-full items-center rounded-sm text-left ${workspace ? 'gap-2 py-3 font-medium' : 'gap-3 py-4'} ${focus}`}
			>
				{workspace ? (
					<>
						<ChevronDown
							size={16}
							aria-hidden="true"
							className={`shrink-0 text-gray-3 transition-transform ${open ? 'rotate-180' : ''}`}
						/>
						<span className="min-w-0 text-base text-gray-1">
							{title}
							{score && <> · {score}</>}
						</span>
					</>
				) : (
					<>
						<span className="min-w-0 flex-1 text-base sm:text-lg">{title}</span>
						{score && (
							<span
								className={`shrink-0 text-lg tabular-nums ${positive ? 'text-green-600' : 'text-amber-600'}`}
							>
								{score}
							</span>
						)}
						<ChevronDown
							size={18}
							aria-hidden="true"
							className={`shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
						/>
					</>
				)}
			</button>
			{open && (
				<div
					className={
						workspace
							? 'ml-6 min-w-0 pb-3 text-base leading-relaxed text-gray-3 [&_.markdown-body]:break-words'
							: 'min-w-0 pb-5 text-gray-500 [&_.markdown-body]:break-words'
					}
				>
					{children}
				</div>
			)}
		</section>
	);
}

function taskStatus(task: CandidateInterviewReportTaskV1) {
	if (task.solved) return 'OK';
	if (task.status === 'not_started') return 'Не начата';
	if (task.status === 'skipped') return 'Пропущена';
	if (task.status === 'timed_out') return 'Время вышло';
	return 'Не решена';
}

export function CandidateReport({
	interviewId,
	envelope,
	onEvent,
	variant = 'legacy',
}: {
	interviewId: string;
	envelope: CandidateInterviewReportEnvelopeV1;
	variant?: ReportVariant;
	onEvent: CandidateTrack;
}) {
	const workspace = variant === 'workspace';
	const Markdown = workspace ? TheoryMarkdown : ChatMarkdown;
	const report = envelope.status === 'ready' ? envelope.report : undefined;
	const tasks = report?.tasks ?? envelope.tasks;
	const solved = tasks.filter((task) => task.solved).length;
	return (
		<div
			className={
				workspace
					? 'min-w-0 px-5 pb-5 sm:px-7 sm:pb-7'
					: 'min-w-0 px-5 pb-2 sm:px-6'
			}
			aria-live="polite"
		>
			{report ? (
				<>
					{workspace ? (
						<div className="mb-4 min-w-0 rounded-xl border border-green-100 bg-green-50 p-4 sm:p-6">
							<div className="mb-3 flex items-center gap-2 text-sm font-medium text-green-700">
								<Sparkles size={16} aria-hidden="true" />
								AI-отчёт
							</div>
							<p className="mb-2 text-sm text-green-700">
								{reportLabel(report)} · {formatScore(report.totalScore)} из{' '}
								{formatScore(report.maxScore)} баллов
							</p>
							<TheoryMarkdown>{report.summaryMarkdown}</TheoryMarkdown>
						</div>
					) : (
						<div className="mb-5 flex items-start gap-4 rounded-[20px] bg-[#e5f5ed] p-5 text-gray-800 sm:p-6">
							<Sparkles
								className="mt-px size-6 shrink-0 text-[#23864e]"
								aria-hidden="true"
							/>
							<div className="min-w-0 break-words leading-relaxed">
								<p className="mb-2 font-medium">
									{reportLabel(report)} · {formatScore(report.totalScore)} из{' '}
									{formatScore(report.maxScore)} баллов
								</p>
								<ChatMarkdown>{report.summaryMarkdown}</ChatMarkdown>
							</div>
						</div>
					)}
				</>
			) : (
				<div
					className="mb-5 rounded-2xl bg-gray-50 p-5 text-gray-600"
					role="status"
				>
					<div className="flex items-center gap-3 font-medium">
						{envelope.status === 'pending' && (
							<LoaderCircle
								size={20}
								className="shrink-0 animate-spin motion-reduce:animate-none"
								aria-hidden="true"
							/>
						)}
						{envelope.status === 'pending'
							? 'Отчёт готовится…'
							: envelope.status === 'error'
								? 'Не удалось подготовить отчёт'
								: 'Для этого собеседования нет отчёта'}
					</div>
					<p className="mt-2 text-sm leading-relaxed">
						{envelope.status === 'pending'
							? 'Результат появится здесь автоматически. Можно вернуться позже.'
							: 'Диалоги собеседования сохранены — их можно открыть ниже.'}
					</p>
				</div>
			)}
			<ReportSection
				variant={variant}
				title="Задачи"
				score={`${solved}/${tasks.length}`}
				positive={solved === tasks.length}
				initialOpen
				onToggle={(open) => onEvent(open ? 'tasks_view' : 'tasks_close')}
			>
				<table className="w-full table-fixed text-left text-sm sm:text-base">
					<thead>
						<tr className="border-b border-gray-200 text-gray-800">
							<th className="w-7 pb-3 font-semibold">#</th>
							<th className="pb-3 font-semibold">Задача</th>
							<th className="w-16 pb-3 font-semibold sm:w-24">Время</th>
							<th className="w-20 pb-3 font-semibold sm:w-28">Статус</th>
						</tr>
					</thead>
					<tbody>
						{tasks.map((task) => (
							<tr
								key={task.position}
								className="border-b border-gray-100 last:border-0"
							>
								<td className="py-3 align-top">{task.position + 1}</td>
								<td className="break-words py-3 pr-3 align-top">
									{task.dialogId ? (
										<Link
											prefetch={false}
											href={`/interviews/${encodeURIComponent(interviewId)}/attempts/${encodeURIComponent(envelope.attemptId)}/items/${task.position}`}
											onClick={() =>
												onEvent('task_open', { position: task.position })
											}
											className={`rounded-sm hover:text-green-700 hover:underline ${focus}`}
										>
											{task.title}
										</Link>
									) : (
										task.title
									)}
								</td>
								<td className="py-3 pr-1 align-top tabular-nums">
									{formatTaskDuration(task.durationSeconds)}
								</td>
								<td className="break-words py-3 align-top">
									{taskStatus(task)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</ReportSection>
			{report?.criteria.map((criterion) => (
				<ReportSection
					variant={variant}
					key={criterion.key}
					title={criterion.title}
					score={`${formatScore(criterion.score)}/${formatScore(criterion.maxScore)}`}
					positive={criterion.score === criterion.maxScore}
					onToggle={(open) =>
						onEvent(open ? 'criterion_view' : 'criterion_close', {
							criterionKey: criterion.key,
						})
					}
				>
					<Markdown>{criterion.explanationMarkdown}</Markdown>
				</ReportSection>
			))}
		</div>
	);
}
